import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SellerOnboardingAlert from "../components/SellerOnboardingAlert";
import { webinarsService } from "../features/webinars/service";
import type { ParsedApiError } from "../lib/http/apiErrors";
import { parseApiError } from "../lib/http/apiErrors";

const WebinarPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [presenter, setPresenter] = useState("");
  const [webinarDate, setWebinarDate] = useState("");
  const [webinarTime, setWebinarTime] = useState("");
  const [webinarLink, setWebinarLink] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);

  const [saving, setSaving] = useState(false);
  const [createdWebinarId, setCreatedWebinarId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorInfo, setErrorInfo] = useState<ParsedApiError | null>(null);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPresenter("");
    setWebinarDate("");
    setWebinarTime("");
    setWebinarLink("");
    setMediaFile(null);
    setCreatedWebinarId(null);
    setErrorMsg("");
    setErrorInfo(null);
  };

  const validate = (): boolean => {
    if (!title.trim()) {
      setErrorMsg("Please enter webinar title.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setErrorInfo(null);
    if (!validate()) return;

    setSaving(true);
    try {
      const created = await webinarsService.createWebinar({
        title: title.trim(),
        description: description.trim(),
        presenter: presenter.trim() || undefined,
        webinar_date: webinarDate || undefined,
        webinar_time: webinarTime || undefined,
        webinar_link: webinarLink.trim() || undefined,
      });

      if (mediaFile) {
        const fileType = mediaFile.type.startsWith("image/") ? "thumbnail" : "recording";
        await webinarsService.uploadWebinarFile(created.id, mediaFile, fileType);
      }

      setCreatedWebinarId(created.id);
    } catch (err) {
      const parsed = parseApiError(err, "Failed to save webinar. Please try again.");
      setErrorMsg(parsed.message);
      setErrorInfo(parsed);
    } finally {
      setSaving(false);
    }
  };

  if (createdWebinarId) {
    return (
      <div className="page-shell flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">📹</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Webinar saved</h2>
            <p className="text-sm text-gray-500 mt-1 font-medium">{title.trim()}</p>
            <p className="text-xs text-gray-400 mt-2">Webinar ID: {createdWebinarId}</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate("/inventory/my")}
              className="w-full py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              My Inventory
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="w-full py-3 bg-rose-600 text-white rounded-xl text-sm font-semibold hover:bg-rose-700 transition-colors shadow-sm"
            >
              + Post another webinar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/deals/create")}
            className="text-gray-500 hover:text-gray-800 text-sm font-medium"
          >
            ← New Listing
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <h1 className="text-xl font-bold text-gray-900">AI Webinar</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500 mb-6">Create a webinar listing and upload one media file (image or video).</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">Webinar title *</span>
            <input
              required
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1">Presenter</span>
              <input
                type="text"
                value={presenter}
                onChange={(e) => setPresenter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1">Date</span>
              <input
                type="date"
                value={webinarDate}
                onChange={(e) => setWebinarDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1">Time</span>
              <input
                type="time"
                value={webinarTime}
                onChange={(e) => setWebinarTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-800 mb-1">Registration link</span>
              <input
                type="url"
                value={webinarLink}
                onChange={(e) => setWebinarLink(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">Description</span>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-sm bg-white resize-none"
            />
          </label>

          <label className="block">
            <span className="block text-sm font-semibold text-gray-800 mb-1">Upload media (image or video)</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              onChange={(e) => setMediaFile(e.target.files?.[0] ?? null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white"
            />
            {mediaFile ? (
              <p className="text-xs text-gray-500 mt-1">
                {mediaFile.name} ({mediaFile.type.startsWith("image/") ? "thumbnail" : "recording"})
              </p>
            ) : null}
          </label>

          {errorMsg && (
            errorInfo?.isSellerOnboardingIncomplete ? (
              <SellerOnboardingAlert
                message={errorInfo.message}
                missingFields={errorInfo.missingFields}
                missingFieldsDetailed={errorInfo.missingFieldsDetailed}
                returnTo={location.pathname}
              />
            ) : (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{errorMsg}</div>
            )
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-xl text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Post webinar"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default WebinarPage;
